export const steps = {
  title: 'Steps to Accept an Admission Offer',
  desc: `<p>Please complete the following procedures to take up the offer <b>in two weeks from the issue date of the notice of offer</b>:</p>`,
  content: `
        <p>1. Indicate your acceptance by clicking "Accept My Offer". </p>
        <p>2. Settle the registration fee and the required fees before the payment deadline (see details in the debit note). </p>
        `,
};

export const title = (academicYear: string): string => `
        We are pleased to offer you a place to the following programme for ${academicYear}
    `;

export const ending = `
        <p>We look forward to seeing you at PolyU!</p>
        <br/>
        <p>Graduate School</p>
        <p>The Hong Kong Polytechnic University</p>
    `;

export const remarksToApplicant = ({
  supervisor,
  researchArea,
  deadline,
  conditions,
}: {
  supervisor: string;
  researchArea: string;
  deadline: string;
  conditions: string[];
}): string => `
        <p>${supervisor} will be your temporary supervisor and your research area is ${researchArea}.</p>
        <p>${conditions.map(item => item).join('<p/><p>')} </p>
        <p>To meet the condition(s) or otherwise, please return to this Notice of Offer page and complete the procedures in the "Meeting Condition(s)" section, which will be shown after accepting the offer, when you have acquired the relevant qualifications on or before <u>${deadline}</u>. [See <u class="itatic">Note</u> below if you are not able to meet the condition(s)]. If you fail to do so, your place will be offered to another eligible applicant.</p>
        <p>For queries on the above condition(s), please contact Mr. Gary SHAM by telephone at 3400 8582 or by email to <i class="email-link">gary-ky.sham@polyu.edu.hk</i>.</p>
        <p><u class="italic">Note:</u> <i>For applicants who cannot meet the condition(s), the University will arrange a refund of the Regisitration Fee paid by cheque (in Hong Kong Dollars), if relevant supporting documents are submitted on or before the stated deadline.</p></i>
    `;

export const fellowshipAndScholarship = (): string => `
    <p>Please refer to this <i class="color">letter</i> for details of the Fellowship/Scholarship (if any), tution fee and caution money. Information about other fees associated with research postgraduate programmes is available at the <a class="webside-link" href="https://www.polyu.edu.hk/gs/current-students/tuition-fees-and-other-fees/">Graduate School's website</a>.</p>
    <p>For enquires, please contact the Graduate School by email to <i class="email-link">gs.enquiry@polyu.edu.hk</i>.</p>
    `;

export const offerAcceptance = (): string => `
    <p>To accept this offer, please pay the required fees (see details in the <i class="color">debit note</i>) and indicate your acceptance by clicking "Accept Offer" on or before the <i class="color">payment deadline</i>. By accepting the offer, you agree to comply with the <i class="color">regulations of the University</i>. If you do not wish to accept the offer, please let us know by clicking "Decline Offer" to enable early release of the study place to another eligible applicant.</p>
    <p>If you have any enquires concerning this offer, please contact us at <i class="email-link">ar.rpg@polyu.edu.hk</i>.</p>
    `;

export const facilitate = (academicYear: string): string => `
    <ol style="list-style: decimal; margin-left: 24px;">
        <li style="margin: 12px 0;">Useful Information for New Research Postgraduate Students</li>
        <li style="margin: 12px 0;">Commencement of the ${academicYear} - New Research Postgraduate Students</li>
    </ol>
    `;

export const OFFER_TEXT = {
  OFFER_INFORMATION: 'Offer Information',
  OFFER_STATUS_LABEL: 'Offer Status',
  DEPARTMENT: 'Department/School',
  PROGRAMME: 'Programme',
  STUDY_MODE: 'Mode of Study',
  PROGRAMME_CODE: 'Programme Code',
  STUDY_PERIOD: 'Normal Period of Study',
  CREDIT_REQUIREMENT: 'Credit Requirements',
  CHIEF_SUPERVISOR: 'Chief Supervisor',
  STUDY_COMMENCEMENT: 'Study Commencement',
  REPORTING_DATE: 'Date of Reporting Study',
  OFFER: 'Offer',
  CONDITION_OFFER: 'Conditional Offer',
  FIRM_OFFER: 'Firm Offer',
  CONDITIONS: 'Condition(s) for Admission',
  CONDITIONS_DEADLINE: 'Deadline for Meeting the Condition(s)',
  STUDENT_VISA_PERMIT:
    'Student Visa/Entry Permit Application (For non-local students only)',
  STUDENT_VISA_PERMIT_CONTENT: `
    Please ensure you have obtained a valid student visa/entry permit for pursuing study at PolyU before you complete programme registration. 
    If you are required to apply for a student visa, please submit your <a href = "https://www.polyu.edu.hk/aradm/rpg/instructions_for_applying_student_visa.pdf"
    target="_blank">Student Visa Application Form</a> and all the required documents <b>by express post within 10 working days of the issuance date of the offer or conditional offer of admission</b>. 
    The Hong Kong Immigration Department takes at least six weeks (even a longer time during the summer peak season) to process your student visa application upon receipt of all the required documents. 
    You need to show us the original of your travel document with e-Visa (i.e. Notification Slip for Entry Visa / Permit) and the landing slip issued by the Hong Kong Immigration Department when you collect your student identity card at PolyU.
    <br><br>
    You may check the latest status of your student visa application via RPgAdmission System from time to time. Once your visa application is approved, you will receive an email notification on how to download your e-Visa and No Objection Letter (if applicable) from the Hong Kong Immigration Department’s website. However, if you are given a conditional offer, you will only receive the email notification after you have met the condition(s) of your admission offer. `,
  REGISTRATION_PROCEDURES: 'Registration Procedures',
  REGISTRATION_PROCEDURES_CONTENT_2024_02_03: `
    <p>
    Before proceeding to programme registration, you are required to arrange for the following documents to be sent directly by the issuing authority to the Academic Registry of PolyU for verification. <b>Please note that all qualifications (except those obtained from PolyU) mentioned in your application should be submitted for verification. Failure to do so or any discrepancies found may lead to nullification of your offer and registration at the University. Fees paid will not be refunded.</b>
  </p>
  <table style="border-collapse: collapse">
    <tr>
      <td style="border: 1px solid;padding:12px; vertical-align: top">Qualifications from Chinese Mainland</td>
      <td style="border: 1px solid;padding:12px">
        <p style="text-decoration: underline">Official Final Transcripts</p>
        <ul>
          <li>
            <p>The official final transcripts should be sent directly by the issuing authority to PolyU. The transcript should include results of all the coursework and project(s), a final GPA/grade/score together with an explanation of the
              grading system.</p>
            <p>If your final score and GPA or the explanation of the grading system is not shown on your final transcript, please request your institution to submit a letter of certificate certifying your final score and GPA together with
              an explanation of the grading system issued by the Registrar or an authorised official of your institution. The letter should include your name, date of birth and the title and signature of an authorised official with an
              official stamp of your institution.</p>
          </li>
        </ul>
        <p style="text-decoration: underline">Degree Certificates/Diplomas</p>
        <ul>
          <li>
            <div>
              <p>For degrees awarded by universities in the Chinese Mainland/recognised by the Ministry of Education from Chinese Mainland, your Degree Certificate (學位證書) and Academic Credential (畢業證書) will be verified via official online
                platforms directly. Please input the Online Verification Code (線上驗證碼) issued by the China Academic Degrees and Graduate Education Development Center (教育部學位與研究生教育，發展中心) of your Degree Certificate (學位證書) and the Online
                Verification Code (線上驗證碼) of your Academic Credential (畢業證書) issued by the CHESICC (學信網) at RPgAdmission. Since the Online Verification Code
                (線上驗證碼) of your Academic Credential (畢業證書) will expire 180 days after it is sent to you, please ensure that the code is still valid at least 14 days before the first day of your online programme registration period
                and extend the validity period of your code (<a href="https://www.chsi.com.cn/xlcx/rhyg.jsp">https://www.chsi.com.cn/xlcx/rhyg.jsp</a>), if deemed necessary.</p>
            </div>
          </li>
        </ul>
      </td>
    </tr>

    <tr>
      <td style="border: 1px solid;padding:12px;vertical-align: top">
        Qualifications from Hong Kong or other countries
      </td>
      <td style="border: 1px solid;padding:12px">
        <p style="text-decoration: underline">Official Final Transcripts</p>
        <ul>
          <li>
            <div>
              <p>The official final transcripts should be sent directly by the issuing authority to PolyU. The transcript should include results of all the coursework and project(s), a final GPA/grade/score together with an explanation
                of the grading system.</p>
              <p>If your final score and GPA or the explanation of the grading system is not shown on your final transcript, please request your institution to submit a letter of certificate certifying your final score and GPA together
                with an explanation of the grading system issued by the Registrar or an authorised official of your institution. The letter should include your name, date of birth and the title and signature of an authorised official
                with an official stamp of your institution.</p>

            </div>
          </li>
        </ul>
        <p style="text-decoration: underline">Degree Certificates/Diplomas</p>
        <ul>
          <li>
            <p>The officially certified true copies of degree certificates/diplomas should be sent directly by the issuing authority to PolyU. If the issuing authority is not able to send your transcript to PolyU directly, you should request
              the institution to put the transcript in a sealed and stamped envelope for your onward transmission to PolyU.</p>
          </li>
        </ul>
      </td>
    </tr>
  </table>
  <ul>
    <li>You are strongly advised to arrange for the above documents to be sent directly by a central unit of the issuing authority (e.g. Registry or equivalent) to PolyU as soon as possible (i.e. right after accepting the admission offer).</li>
    <li><span style="color:red"><b>Electronic copies are highly preferred.</b></span> Please ask the issuing authority to send us the electronic copies of your documents using an official email account (e.g. ar.rpg@polyu.edu.hk).</li>
    <li>If the issuing authority cannot send us an electronic version of the official documents, please ask them to send us hard copy of the official documents by express post. The envelope should be sealed, signed and stamped by the Registrar or
      by an authorised official of your institution.</li>
  </ul>
  <table style="border-collapse: collapse;border: 1px solid black">
    <tr style="border: 1px solid black">
      <td style="border: 1px solid black">Email Address:</td>
      <td style="border: 1px solid black"><a style="color:blue;text-decoration:underline">ar.rpg@polyu.edu.hk</a></td>
    </tr>
    <tr style="border: 1px solid black">
      <td style="border: 1px solid black">Mailing Address:</td>
      <td style="border: 1px solid black">Academic Registry, M101, Li Ka Shing Tower, The Hong Kong Polytechnic University, Kowloon, Hong Kong</td>
    </tr>
    <tr style="border: 1px solid black">
      <td style="border: 1px solid black">Contact Number:</td>
      <td style="border: 1px solid black">+852 2333 0600</td>
    </tr>
  </table>
  <ul>
    <li>The University will only accept originals or copies of the documents that have been duly declared as true copies by an authorised party, which includes a lawyer, a notary public*, or a Consulate General.</li>
    <li>Official certified translation in English is required for transcripts and certificates that are not in English or Chinese. The translator should sign and stamp on both (i) the translation and (ii) the official document and certify that the
      translation is accurate and completed by an authorised party as stated above.</li>
    <li>All documents submitted will not be returned. Please do not send us any <span style="color:red"><b>originals which cannot be re-issued</b></span>.</li>
    <li>You may be asked to re-submit any official certification of qualifications (e.g. transcript or degree award certificate, etc.) if deemed necessary.</li>
  </ul>
  <p style="margin-top:14pt;margin-bottom:14pt;line-height:normal"><span style="text-decoration: underline;">Score Reports of GMAT, GRE, IELTS or TOEFL</span></p>
  <p><b>If your score reports can be verified online</b>, you should inform the relevant test/examination provider to release the right to PolyU to verify your results online. Below are the PolyU codes for GMAT, GRE and TOEFL:</p>
  <table>
    <tr>
      <td>GMAT</td>
      <td>CB8</td>
    </tr>
    <tr>
      <td>GRE</td>
      <td>3404</td>
    </tr>
    <tr>
      <td>TOEFL*</td>
      <td>0732</td>
    </tr>
  </table>
  <p>* Please make sure that online results can be verified before your assigned online programme registration period.</p>
  <p>IELTS score report could be verified online without an access code. You are therefore not required to request IELTS to send us the hard copy report unless you are asked to do so.</p>
  <p><b>If your score reports CANNOT be verified online</b>, you should ask the relevant test/examination provider to send us the fficial result slip for our verification.</p>
  <p>If you fail to complete the necessary procedures, or if your fee payment has not been properly received in full, the University will release your study place to other eligible applicants immediately. All fees paid will not be refunded. For reinstatement
    of offer, you must submit an application to your department for consideration. </p>
  <p>You will get a PolyU Student Identity Card after completing the registration procedures. Your data together with the photo image (collected upon programme registration) will then be transferred to the Student Record System of PolyU. These will
    be used in activities and services provided by units of the University in support of your study at PolyU including those conducted by the student organisations and application for student residence. </p>
  <p>Your data may also be transferred to parties outside PolyU for processing student visa (for non-local students) and/or any other activities related to your study (such as placement, field trips, internship, exchange programme, Service Learning
    and Work Integrated Education arrangements and training). </p>
  <p>Your contact information will be used for maintaining on-going communication with you after your graduation, including the delivery of announcement and other information, such as activities, education programmes, benefits and services.</p>
  
  `,
  REGISTRATION_PROCEDURES_CONTENT_2025_01_02_03: `
    <p>
      Please refer to the Guidelines on Submission of Required Documents for
      Qualification (<a
        target="_blank"
        rel="noreferrer"
        href="https://www.polyu.edu.hk/gs/docdrive/Others/guidelines_qualification_verification_rpg.pdf"
        style="color: blue; text-decoration: underline"
        >link</a
      >) for details.
      <b
        >Please note that all qualifications(except those obtained from PolyU)
        mentioned in your application should be submitted for verification.
        Failure to do so or any discrepancies found may lead to nullification
        of your offer and registration at the University. Fees paid will not
        be refunded.</b
      >
      You can only proceed with online programme registration after completion
      of qualification verification and approval of student visa application
      (if applicable).
    </p>`,
};
